import React, { useContext, useEffect, useRef, useState } from "react";
import { Button, Row, Card, Col, Collapse, Table, Form } from "react-bootstrap";
import { abpnClient } from "../../../Services/AbpnClient";
import { CreditCardClientLibrary, getCreditCardProcessor, getPaymentProcessorForPaymentType } from "../../../Services/PaymentProcessor/PaymentProcessor";
import Payment from "../../../Services/PaymentProcessor/Payment";
import { AuthContext } from "../../Auth/AuthProvider";
import Physician from "../../Physician/Models/Physician";
import { deepCopy } from "../../Shared/Utility/DeepCopy";
import ApplicationModel, { CandidateStatus } from "../Models/ApplicationModel";
import ApplicationReceiptModel from "../Models/ApplicationReceiptModel";
import Examination from "../Models/Examination";
import Fee, { FeeType } from "../Models/Fee";
import { Alert } from 'react-bootstrap';
import { Fragment } from "react";
import OrderLineItem from "../../Shared/Models/OrderLineItem";
import { AbpnSpinner } from './../../Shared/Components/AbpnSpinner';
import { PaymentContainer } from "../../../Services/PaymentProcessor/PaymentContainer";
import { getDateWithOffset } from "../../Shared/Utility/GetDateWithOffset";

interface IPaymentCardProps {
    application: ApplicationModel;
    physician: Physician;
    examination: Examination;
    isSectionAvailable: boolean;
    isSectionOpen: boolean;
    onApplicationSubmitted: (applicationReceipt: ApplicationReceiptModel) => void;
    examinationDates: string;
    displayBanner: () => void;
    onOpen: () => void;
    disabled: boolean;
    applicationDeadlineDate: string;
    totalBalance: number;
}

interface IPaymentPlan {
    dueNow: number;
    isPayInFull: boolean;
    isPayInSplit: boolean;
}

interface ISelectedInstallmentPayments {
    total: number;
    isFirstInstallmentSelected: boolean;
    isSecondInstallmentSelected: boolean;
}

export function PaymentCard(props: IPaymentCardProps) {
    const hasExistingOrder = props.application.order !== null;
    const orderWithFirstPaymentDue = hasExistingOrder && (props.application.order.total === props.application.order.balance)

    const [openCollapse, setOpenCollapse] = useState<boolean>(props.isSectionOpen);

    const contactAddress = props.physician.addresses.addresses.find(_ => _.isContact);

    const [payment, setPayment] = useState<Payment>({ email: props.physician.emailAddress, city: contactAddress?.city, state: contactAddress?.stateCode } as Payment);
    
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [isPaymentSuccessful, setIsPaymentSuccessful] = useState<boolean>(null);
    const [isDisable, setIsDisable] = useState<boolean>(false);
    const [creditCardProcessor, setCreditCardProcessor] = useState<CreditCardClientLibrary>(CreditCardClientLibrary.None);
    
    const payContainerRef = React.useRef(null);

    const formRef = useRef(null);

    const today = new Date();

    useEffect(() => {
        if (creditCardProcessor !== CreditCardClientLibrary.None) return;
        
        const getCreditCardProcessorForPaymentContainer = async () => {
            const cardProcessor : CreditCardClientLibrary = await getCreditCardProcessor();
            setCreditCardProcessor(cardProcessor);
        }
    
        getCreditCardProcessorForPaymentContainer();
    
    }, [])
   
        
    const isPastDeadline = (date: string) => {
        var deadlineDate = getDateWithOffset(date);
        return getDateWithOffset(deadlineDate.toDateString()) < getDateWithOffset(today.toDateString());
    }

    const getNonExamAndApplicationFees = () => {
        var otherFees = props.application.order?.total - props.application.totalExamFeeAmount;

        var totalInstallmentsAmount = 0;

        if (props.application.installmentOptions.length > 0) {
            totalInstallmentsAmount = props.application.installmentOptions.map(_ => _.amount)?.reduce((a, b) => a + b, 0);
        }

        if (totalInstallmentsAmount > 0 && totalInstallmentsAmount === props.application?.order?.balance) return 0;

        if (props.application.installmentOptions?.length === 2) {
            otherFees = props.application?.order?.balance - totalInstallmentsAmount;
        }

        return otherFees > 0 ? otherFees : 0;
    }

    const getPaymentPlanAmountDueNow = () => {
        if (!isSplitPay) return props.application?.order?.balance;

        var isFirstInstallmentPastDue = isPastDeadline(props.application.installmentOptions[0]?.dueDate);
        var isSecondInstallmentPastDue = props.application.installmentOptions[1] && isPastDeadline(props.application.installmentOptions[1].dueDate);

        if (isFirstInstallmentPastDue && isSecondInstallmentPastDue) return props.application?.order?.balance;

        var installmentAmountDue = orderWithFirstPaymentDue ? 0 : props.application?.installmentOptions?.[0]?.amount;

        return installmentAmountDue + getNonExamAndApplicationFees();
    }

    const [paymentPlan, setPaymentPlan] = useState<IPaymentPlan>({
        dueNow: props.totalBalance,
        isPayInFull: false,
        isPayInSplit: false,
    })
    const isSplitPay = (props.application.isSplitPay || paymentPlan.isPayInSplit);
    const existingOrderWithSplitPay = hasExistingOrder && isSplitPay;

    const hasPaymentPlan = (paymentPlan.isPayInSplit || paymentPlan.isPayInFull);
    const existingOrderWithFirstPaymentDue = hasExistingOrder && orderWithFirstPaymentDue;

    const hasBalanceDue = paymentPlan.dueNow > 0;

    const selectPaymentPlanForExistingOrder = (existingOrderWithFirstPaymentDue && hasBalanceDue);
    const canSelectPaymentPlan = (!hasExistingOrder || selectPaymentPlanForExistingOrder) && !props.examination.isMoc && props.application.candidateStatus != CandidateStatus.InviteSent && hasBalanceDue;

    const [selectedInstallmentPayments, setSelectedInstallmentPayments] = useState<ISelectedInstallmentPayments>({
        total: getPaymentPlanAmountDueNow(),
        isFirstInstallmentSelected: !orderWithFirstPaymentDue,
        isSecondInstallmentSelected: false,
    })

    const authContext = useContext(AuthContext);

    const setPayInFull = () => {
        var updated = deepCopy(paymentPlan);
        updated.isPayInFull = true;
        updated.isPayInSplit = false;
        updated.dueNow = props.totalBalance;
        setPaymentPlan(updated);
    }

    const setSplitPayment = () => {
        var updated = deepCopy(paymentPlan);
        updated.isPayInFull = false;
        updated.isPayInSplit = true;
        updated.dueNow = props.totalBalance - props.application.installmentOptions.map(_ => _.amount).reduce((a, b) => a + b, 0);
        setPaymentPlan(updated);
    }
    const onPaymentChanged = (updated: Payment) => {
        setPayment(deepCopy(updated));
    }
    const onFormSubmitPayment = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        event.stopPropagation();

        if (isSubmitting) {
            return;
        }

        event.currentTarget.classList.add('was-validated');

        submitPayment();
    }

    const submitPayment = async () => {
        setIsDisable(true);

        if (isSubmitting) {
            return;
        }

        if (payContainerRef === null || payContainerRef === undefined || !hasBalanceDue)
        {
                setIsSubmitting(true);
                var applicationReceipt = await abpnClient.get<ApplicationReceiptModel>('/api/application/submit?examinationId=' + props.application.examinationId, authContext)

                props.onApplicationSubmitted(applicationReceipt);
                return;
        }

        if (payContainerRef.current.validate(payment) && formRef.current.checkValidity()) {
            formRef.current.classList.remove('was-validated');

            var updatedPayment = deepCopy(payment);
            updatedPayment = await getPaymentProcessorForPaymentType(creditCardProcessor, updatedPayment.paymentType).tokenize(updatedPayment);

            var isInstallmentPayment = (hasExistingOrder && isSplitPay && !existingOrderWithFirstPaymentDue)
            updatedPayment.amount = isInstallmentPayment ? selectedInstallmentPayments?.total : paymentPlan.dueNow;

            setPayment(updatedPayment);

            if (!updatedPayment?.IsValid) {
                setIsDisable(false);
                return;
            }

            setIsDisable(false);
            setIsSubmitting(true);

            //Installment Payments
            if (hasExistingOrder && props.application.candidateStatus !== CandidateStatus.Unsubmitted) {
                var applicationReceipt = await abpnClient.post<ApplicationReceiptModel>('/api/application/makePayment?applicationId=' + props.application.applicationId, updatedPayment, authContext)

                setIsSubmitting(false);
                setIsPaymentSuccessful(applicationReceipt.isSuccessful);

                if (!applicationReceipt.isSuccessful) {
                    return;
                }

                props.displayBanner();
                return;
            }

            var url = `/api/application/submit?examinationId=${props.application.examinationId}&isSplitPay=${paymentPlan.isPayInSplit}`
            var applicationReceipt = await abpnClient.post<ApplicationReceiptModel>(url, updatedPayment, authContext)

            setIsSubmitting(false);
            setIsPaymentSuccessful(applicationReceipt.isSuccessful);

            if (!applicationReceipt.isSuccessful) {
                return;
            }

            props.onApplicationSubmitted(applicationReceipt);
        }

        setIsDisable(false);
    }

    const getExamLateFee = () => {
        var initialLateFee = props.examination.fees.find(_ => _.feeType === FeeType.lateFeeInitial)?.feeAmount;
        return initialLateFee ?? props.examination.fees.find(_ => _.feeType === FeeType.lateFeeMoc)?.feeAmount;
    }

    const getPaymentDeadlineDate = () => {
        var deadlineDate = props.application.installmentOptions[props.application.installmentOptions?.length - 1]?.dueDate ??
            props.examination.applicationLateDeadlineDate;

        return getDateWithOffset(deadlineDate);
    }

    const getLateFeeDate = (orderLineItem: OrderLineItem) => {
        if (orderLineItem.feeType === FeeType.lateFeeMoc || orderLineItem.feeType === FeeType.lateFeeInitial) {
            var formattedDate = getDateWithOffset(orderLineItem.createdDate).toLocaleDateString('en-us', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
            });
            return `(${formattedDate})`;
        }
        return '';
    }

    const getFeeDescription = (fee: Fee) => {
        var isWaived = fee.feeAmount < 0;
        var isDiscount = (fee.feeType == FeeType.ccAnnualFee) || 
                         (fee.feeType == FeeType.multipleExamDiscount) ||
                         (fee.feeType == FeeType.committeeMemberDiscount) ||
                         (fee.feeType == FeeType.committeeMemberLateFeeDiscount) ||
                         (fee.feeType == FeeType.committeeMemberProcessingFeeDiscount) ||
                         (fee.feeType == FeeType.committeeMemberTransferFeeDiscount);

        if (isWaived && !isDiscount) return `${fee.feeDescription} (Waived)`;
        return fee.feeDescription;
    }

    const updateInstallmentsSelectedTotal = (amount: number) => {
        var updated = deepCopy(selectedInstallmentPayments);
        updated.total = !updated.isSecondInstallmentSelected ? (updated.total + amount) : (updated.total - amount);
        updated.isSecondInstallmentSelected = !updated.isSecondInstallmentSelected;
        setSelectedInstallmentPayments(updated);
    }

    useEffect(() => {
        setOpenCollapse(props.isSectionOpen);
    }, [props.isSectionOpen]);

    useEffect(() => {
        if (openCollapse) props.onOpen();
    }, [openCollapse]);

    useEffect(() => {
        const resetPaymentPlan = () => {
            var updated = deepCopy(paymentPlan);
            updated.isPayInFull = false;
            updated.isPayInSplit = false;
            updated.dueNow = props.totalBalance;
            setPaymentPlan(updated);
        }

        resetPaymentPlan();
    }, [props.totalBalance]);

    const isFormDisabled = isDisable || (!payment.paymentType && hasBalanceDue);

    return (
        <Card className="mb-4" border="pp-secondary-grey">
            <Card.Body>
                <Card.Title className="mb-0 font-weight-bolder" onClick={() => { if (!props.disabled) setOpenCollapse(!openCollapse) }}>
                    <span className={`text-pp-primary ${props.disabled && 'pp-disabled'}`}><i className="font-weight-bolder las la-angle-down"></i> Payment</span>
                </Card.Title>
                <Collapse in={openCollapse}>
                    <Form noValidate onSubmit={(e) => onFormSubmitPayment(e)} ref={formRef}>
                        {isSubmitting ? <AbpnSpinner /> :
                            <Row className="mt-3">
                                <Col xs={12} md={4} lg={4}>
                                    <Card.Text className="font-weight-bolder mb-1 pp-font-size-smaller">
                                        Applicant Name
                                    </Card.Text>
                                    <Card.Text className="pp-font-size-smaller">
                                        {props.physician.fullName}
                                    </Card.Text>
                                    <Card.Text className="font-weight-bolder mb-1 pp-font-size-smaller">
                                        Email
                                    </Card.Text>
                                    <Card.Text className="pp-font-size-smaller">
                                        {props.physician.emailAddress}
                                    </Card.Text>
                                    <Card.Text className="font-weight-bolder mb-1 pp-font-size-smaller">
                                        Examination Requested
                                    </Card.Text>
                                    <Card.Text className="pp-font-size-smaller">
                                        {props.examination.title}
                                    </Card.Text>
                                    <Card.Text className="font-weight-bolder mb-1 pp-font-size-smaller">
                                        Examination Dates
                                    </Card.Text>
                                    <Card.Text className="pp-font-size-smaller">
                                        {props.examinationDates}
                                    </Card.Text>


                                    {!hasExistingOrder &&
                                        <Fragment>
                                            <Card.Text className="font-weight-bolder mb-1 pp-font-size-smaller">
                                                <div>Fees</div>
                                            </Card.Text>
                                            <Table variant="sm" className="pp-fee-table pp-font-size-smaller">
                                                <tbody>
                                                    {props.application.fees.map(_ => <tr key={_.feeType}>
                                                        <td >{getFeeDescription(_)}</td>
                                                        <td className="text-right">${_.feeAmount}</td>
                                                    </tr>)}
                                                    <tr>
                                                        <th className="font-weight-bolder">Total</th>
                                                        <td className="text-right">${props.application?.totalFeeAmount}</td>
                                                    </tr>
                                                </tbody>
                                            </Table>
                                        </Fragment>}

                                    {hasExistingOrder &&
                                        <Table variant="sm" className="pp-fee-table pp-font-size-smaller">
                                            <tbody>
                                                {props.application.order.orderLineItems.map((_, index) => <tr key={index}>
                                                    <td className="font-weight-bolder">{`${_.description} ${getLateFeeDate(_)}`}</td>
                                                    <td className="text-right font-weight-bolder">${_.amount}</td>
                                                </tr>)}
                                                <tr>
                                                    <th className="font-weight-bolder">TOTAL</th>
                                                    <td className="text-right font-weight-bolder">${props.application?.order.total}</td>
                                                </tr>
                                                {props.application.order.completedPayments.map((_, index) => <tr key={index}>
                                                    <td >Paid {(getDateWithOffset(_.transactionDate)).toLocaleDateString('en-us', {
                                                        year: 'numeric',
                                                        month: 'long',
                                                        day: 'numeric'
                                                    })}</td>
                                                    <td className="text-right">(${-1 * _.amount})</td>
                                                </tr>)}
                                            </tbody>
                                        </Table>
                                    }

                                    {(hasExistingOrder && !orderWithFirstPaymentDue && props.application.installmentOptions?.length <= 1 && hasBalanceDue) &&
                                        <Fragment>
                                            <Table variant="sm" className="pp-fee-table pp-font-size-smaller">
                                                <tbody>
                                                    <tr>
                                                        <th className="font-weight-bolder text-pp-primary border-0">Pay Now</th>
                                                    </tr>
                                                    <tr>
                                                        <td className="font-weight-bolder border-0">Due {getPaymentDeadlineDate().toLocaleDateString('en-us', {
                                                            year: 'numeric',
                                                            month: 'long',
                                                            day: 'numeric'
                                                        })} ${props.application.order.balance}</td>
                                                    </tr>
                                                    <tr>
                                                        <th className="font-weight-bolder pp-highlighted border-0">Total ${props.application.order.balance}</th>
                                                    </tr>
                                                </tbody>
                                            </Table>
                                        </Fragment>}
                                    {(existingOrderWithSplitPay && !orderWithFirstPaymentDue && props.application.installmentOptions?.length > 1) &&
                                        <Fragment>
                                            <Card.Text className="font-weight-bolder text-pp-primary mb-2 pp-font-size-smaller">
                                                Select Installment(s) to pay.
                                            </Card.Text>
                                            <Table variant="sm" className="pp-fee-table pp-font-size-smaller">
                                                <tbody>
                                                    {props.application.installmentOptions.map((_, index) =>
                                                        <tr key={index}>
                                                            <Form.Check
                                                                type="checkbox"
                                                                defaultChecked={isPastDeadline(_.dueDate) || index === 0}
                                                                disabled={isPastDeadline(_.dueDate) || index === 0}
                                                                className="mb-2"
                                                                onChange={() => updateInstallmentsSelectedTotal(_.amount)}
                                                                label={<span><b>Due {(getDateWithOffset(_.dueDate)).toLocaleDateString('en-us', {
                                                                    year: 'numeric',
                                                                    month: 'long',
                                                                    day: 'numeric'
                                                                })}</b> ${index === 0 ? _.amount + getNonExamAndApplicationFees() : _.amount}</span>} />
                                                        </tr>)}
                                                    <tr>
                                                        <th className="font-weight-bolder pp-highlighted">Total ${selectedInstallmentPayments.total ?? props.application?.order.balance}</th>
                                                    </tr>
                                                </tbody>
                                            </Table>
                                        </Fragment>
                                    }
                                    {canSelectPaymentPlan &&
                                        <Fragment>
                                            <Card.Text className="font-weight-bolder mb-1 pp-font-size-smaller" hidden={props.examination.isMoc}>
                                                Select Payment Option<span className="text-pp-primary ml-2">*</span>
                                            </Card.Text>
                                            <Button
                                                variant={paymentPlan.isPayInFull ? "pp-primary" : "outline-pp-primary"}
                                                className="mt-1"
                                                onClick={() => setPayInFull()}
                                                hidden={props.examination.isMoc}>Pay In Full</Button>
                                            <Button
                                                variant={paymentPlan.isPayInSplit ? "pp-primary" : "outline-pp-primary"}
                                                className="mt-1"
                                                onClick={() => setSplitPayment()}
                                                disabled={isPastDeadline(props.examination.applicationLateDeadlineDate) || props.application.installmentOptions.length == 0}
                                                hidden={props.examination.isMoc}>Split Payment</Button>
                                        </Fragment>
                                    }
                                    {((canSelectPaymentPlan && hasPaymentPlan) || (!canSelectPaymentPlan && !existingOrderWithSplitPay)) &&
                                        <Fragment>
                                            <Card.Text className="font-weight-bolder mb-1 pp-font-size-smaller mt-4">
                                                Amount Due
                                            </Card.Text>
                                            <Table variant="sm" className="pp-fee-table pp-font-size-smaller">
                                                <tbody>
                                                    <tr>
                                                        <th>Due {props.application.isTransferredIn ? getDateWithOffset(props.applicationDeadlineDate).toLocaleDateString() : `Now`}</th>
                                                        <td className="text-right">${paymentPlan.dueNow}</td></tr>
                                                    {paymentPlan.isPayInSplit && props.application.installmentOptions.map((_, index) =>
                                                        <tr key={index}>
                                                            <td >Due {getDateWithOffset(_.dueDate).toLocaleDateString('en-us', {
                                                                year: 'numeric',
                                                                month: 'long',
                                                                day: 'numeric'
                                                            })}</td>
                                                            <td className="text-right">${_.amount}</td>
                                                        </tr>)}
                                                </tbody>
                                            </Table>
                                            {isSplitPay &&
                                                <div>
                                                    <Card.Text className="pp-font-size-smallest mt-3">
                                                        NOTE: ABPN will NOT automatically charge your credit card or checking account for remaining installment payments.
                                                        You will receive an email with instructions for each installment.
                                                    </Card.Text>
                                                    <Card.Text className="pp-font-size-smallest">
                                                        Any installment payment made after the due date must also include a ${getExamLateFee()} late fee charge.
                                                    </Card.Text>
                                                    <Card.Text className="pp-font-size-smallest">
                                                        If all payments are not received by {getPaymentDeadlineDate().toLocaleDateString('en-us', {
                                                            year: 'numeric',
                                                            month: 'long',
                                                            day: 'numeric'
                                                        })} your application will be denied.
                                                    </Card.Text>
                                                </div>
                                            }
                                        </Fragment>
                                    }

                                    {(!canSelectPaymentPlan || hasPaymentPlan) && hasBalanceDue &&
                                        <Fragment>
                                            <PaymentContainer disabled={isFormDisabled} ref={payContainerRef} payment={payment} onPaymentChanged={onPaymentChanged} onPaymentTypeChanged={() => null} />
                                            {payment.paymentType && payment.IsValid === false &&

                                                <Alert className="pp-font-size-smaller" variant="pp-alert-danger">
                                                    <p><b>Could Not Validate Payment Method.</b> Please verify your payment method and try again</p>
                                                </Alert>}
                                            {isPaymentSuccessful === false &&
                                                <Alert className="pp-font-size-smaller" variant="pp-alert-danger">
                                                    <p><b>The provided payment method failed.</b> Please verify your payment method and try again</p>
                                                </Alert>}

                                        </Fragment>
                                    }

                                    {!isSubmitting &&
                                        <div>
                                            <Button type="submit"
                                                className="mt-4"
                                                variant="pp-primary"
                                                disabled={isFormDisabled}>{`${(hasExistingOrder && hasBalanceDue) ? "Submit Payment" : "Submit Application"}`}
                                            </Button>
                                        </div>
                                    }
                                </Col>
                            </Row>
                        }
                    </Form>
                </Collapse>
            </Card.Body>
        </Card >
    );
}

export default PaymentCard;
